import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Globe, LifeBuoy, LogOut, MonitorDown } from 'lucide-react';
import { DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '@repo/ui/src/components/ui/dropdown-menu';
import { SheetClose } from '@repo/ui/src/components/ui/sheet';
import { Separator } from '@repo/ui/src/components/ui/separator';
import { useAuthStore } from '../../stores/authStore';
import { useLogout } from '../../hooks/useAuth';
import { useSubscription } from '../../hooks/useSubscription';
import { useBankAccounts } from '../../hooks/useBankAccounts';
import { useCan } from '../../hooks/useCan';
import { useHelpDialogStore } from '../../stores/helpDialogStore';
import { useInstallPrompt } from '../../hooks/useInstallPrompt';
import { useInstallHelpStore } from '../../stores/installHelpStore';
import { orgMenuGroups, planLabel, type OtherOrg } from './orgMenuItems';
import { OrgAvatar } from './OrgAvatar';
import { RolePill } from './RolePill';

// File is `OrgMenuPanel.tsx`, not `OrgMenuItems.tsx` as in the original brief:
// this directory already has `orgMenuItems.ts` (the pure model), and the two
// names differ only in the leading letter's case. This repo's tsconfig sets
// `forceConsistentCasingInFileNames`, and on a case-insensitive filesystem
// (default macOS/Windows) that turns into a hard `tsc` error the moment both
// files exist side by side — not a style nit, a build break. The exported
// component name stays `OrgMenuItems`, matching the brief's interface.
type Variant = 'menu' | 'sheet';

const rowClass =
  'flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium text-surface-800 md:min-h-0 md:py-1.5';

// Navigating rows: a menu item on desktop, a self-closing sheet link on phones
// (MoreSheet's SheetClose already handles the close — no manual state poke).
// Module-scope, not declared inside OrgMenuItems: react-hooks/static-components
// (React Compiler) flags a component literal created fresh on every render.
function NavRow({ variant, to, children }: { variant: Variant; to: string; children: ReactNode }) {
  return variant === 'menu' ? (
    <DropdownMenuItem asChild>
      <Link to={to} className="cursor-pointer">{children}</Link>
    </DropdownMenuItem>
  ) : (
    <SheetClose asChild>
      <Link to={to} className={`${rowClass} transition-colors hover:bg-surface-100`}>{children}</Link>
    </SheetClose>
  );
}

function ActionRow({
  variant,
  onSelect,
  destructive,
  children,
}: {
  variant: Variant;
  onSelect: () => void;
  destructive?: boolean;
  children: ReactNode;
}) {
  return variant === 'menu' ? (
    <DropdownMenuItem
      onClick={onSelect}
      className={destructive ? 'cursor-pointer text-destructive focus:bg-destructive/10 focus:text-destructive' : 'cursor-pointer'}
    >
      {children}
    </DropdownMenuItem>
  ) : (
    <button
      type="button"
      onClick={onSelect}
      className={`${rowClass} transition-colors ${destructive ? 'text-destructive hover:bg-destructive/10' : 'hover:bg-surface-100'}`}
    >
      {children}
    </button>
  );
}

function GroupLabel({ variant, children }: { variant: Variant; children: ReactNode }) {
  return variant === 'menu' ? (
    <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-wider text-surface-400">{children}</DropdownMenuLabel>
  ) : (
    <p className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-surface-400">{children}</p>
  );
}

function Rule({ variant }: { variant: Variant }) {
  return variant === 'menu' ? <DropdownMenuSeparator /> : <Separator className="my-2" />;
}

/** The organisation menu's contents — header, Tổ chức, the switcher slot,
 *  Tài khoản, and the account actions. Rendered inside a DropdownMenu on
 *  desktop (`variant="menu"`) and inside the phone Thêm sheet
 *  (`variant="sheet"`), so the two surfaces cannot drift.
 *
 *  The sheet variant also carries the "Trợ giúp" row: phones have no sidebar,
 *  so this is their only way into the help dialog. On desktop that item stays a
 *  standalone sidebar item and is deliberately not duplicated here (spec §2). */
export function OrgMenuItems({
  variant,
  otherOrgs = [],
  onSwitchOrg,
  onAfterAction,
}: {
  variant: Variant;
  otherOrgs?: OtherOrg[];
  /** THI-73 hook. Nothing populates `otherOrgs` yet, so nothing calls this yet. */
  onSwitchOrg?: (id: string) => void;
  /** Sheet only: close it after a row that does not navigate (help). */
  onAfterAction?: () => void;
}) {
  const { t, i18n } = useTranslation('common');
  const { t: tBilling } = useTranslation('billing');
  const user = useAuthStore((s) => s.user);
  const organization = useAuthStore((s) => s.organization);
  const role = useAuthStore((s) => s.role);
  const logout = useLogout();
  const setHelpOpen = useHelpDialogStore((s) => s.setOpen);
  const { canPrompt, promptInstall, isIOS, standalone } = useInstallPrompt();
  const setInstallHelpOpen = useInstallHelpStore((s) => s.setOpen);
  // Sheet (phone) only, and never inside the already-installed app. Android
  // with a stashed beforeinstallprompt re-opens the NATIVE prompt — this is
  // what brings the suggestion back after Chrome's own was dismissed. iOS has
  // no API, so the row opens a two-step instruction dialog instead.
  const showInstall = variant === 'sheet' && !standalone && (canPrompt || isIOS);
  const { data: subscription } = useSubscription();
  const canManageSettings = useCan('manage:settings');
  // The bank read is OWNER/ADMIN only (THI-136); a MEMBER's plan line names
  // the plan and nothing else — never "chưa liên kết", which would be a claim.
  const { data: bankAccounts } = useBankAccounts(canManageSettings);

  const groups = orgMenuGroups(role, otherOrgs);
  const plan = planLabel(subscription, { trial: tBilling('planBadge.trial'), free: t('orgMenu.planFree') });
  const planLine = t(
    !canManageSettings ? 'orgMenu.planLine.plain' : bankAccounts && bankAccounts.length > 0 ? 'orgMenu.planLine.auto' : 'orgMenu.planLine.noBank',
    { plan },
  );

  return (
    <>
      {/* 1. Header — non-interactive */}
      <div className="flex items-start gap-3 px-3 py-2.5">
        <OrgAvatar name={organization?.name} logoUrl={organization?.logoUrl} className="size-9" />
        <div className="min-w-0 flex-1 leading-tight">
          <div className="flex items-center gap-1.5">
            <p className="truncate text-[13px] font-semibold text-surface-900">{organization?.name || 'LopHoc.app'}</p>
            {role && <RolePill role={role} />}
          </div>
          <p className="truncate text-[11px] text-surface-400">{planLine}</p>
        </div>
      </div>
      <Rule variant={variant} />

      {/* 2-4. Tổ chức / switcher slot / Tài khoản */}
      {groups.map((group) => (
        <div key={group.key}>
          <GroupLabel variant={variant}>
            {group.kind === 'links' && group.key === 'account'
              ? t('orgMenu.accountGroup', { name: user?.fullName || '' })
              : t(group.label.key, { ns: group.label.ns })}
          </GroupLabel>
          {group.kind === 'switcher'
            ? group.orgs.map((o) => (
                <ActionRow key={o.id} variant={variant} onSelect={() => onSwitchOrg?.(o.id)}>
                  <OrgAvatar name={o.name} logoUrl={o.logoUrl} className="size-5 text-[9px]" />
                  <span className="flex-1 truncate">{o.name}</span>
                </ActionRow>
              ))
            : group.items.map((item) => (
                <NavRow key={item.key} variant={variant} to={item.href}>
                  <item.icon className="size-4 shrink-0 text-surface-500" strokeWidth={1.8} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{t(item.label.key, { ns: item.label.ns })}</span>
                    {item.hint && (
                      <span className="block truncate text-[11px] font-normal text-surface-400">
                        {t(item.hint.key, { ns: item.hint.ns })}
                      </span>
                    )}
                  </span>
                </NavRow>
              ))}
        </div>
      ))}

      {/* Account actions: language, (sheet only) help, then Đăng xuất last */}
      <ActionRow variant={variant} onSelect={() => i18n.changeLanguage(i18n.language === 'vi' ? 'en' : 'vi')}>
        <Globe className="size-4 shrink-0 text-surface-500" strokeWidth={1.8} />
        {i18n.language === 'vi' ? 'English' : 'Tiếng Việt'}
      </ActionRow>
      {variant === 'sheet' && (
        <ActionRow variant={variant} onSelect={() => { setHelpOpen(true); onAfterAction?.(); }}>
          <LifeBuoy className="size-4 shrink-0 text-surface-500" strokeWidth={1.8} />
          {t('help.itemShort')}
        </ActionRow>
      )}
      {showInstall && (
        <ActionRow
          variant={variant}
          onSelect={() => {
            if (canPrompt) void promptInstall();
            else setInstallHelpOpen(true);
            onAfterAction?.();
          }}
        >
          <MonitorDown className="size-4 shrink-0 text-surface-500" strokeWidth={1.8} />
          {t('install.item')}
        </ActionRow>
      )}
      <Rule variant={variant} />
      <ActionRow variant={variant} destructive onSelect={() => logout.mutate()}>
        <LogOut className="size-4 shrink-0" strokeWidth={1.8} />
        {t('user.logout')}
      </ActionRow>
    </>
  );
}
